import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity } from "lucide-react";
import {
  fetchUsageSummary,
  fetchUsageTimeseries,
  fetchUsageScreens,
  fetchUsageHeatmap,
  fetchUsageRetention,
  type UsageClient,
  type UsageQuery,
  type UsageTimeseriesPoint,
  type UsageHeatCell,
  type UsageCohort,
} from "../../api/admin";
import { AtlasPanel } from "../../components/AtlasPanel";
import { StatBlock } from "../../components/StatBlock";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { CLIENT_COLOR, CLIENT_LABEL, ClientGlyph, Segmented, screenLabel } from "./usage-ui";

// Admin usage analytics (spec 14 / ATL-70): first-party screen views for
// signed-in users, split web / iOS / Android. Admin traffic hidden by default.

const CLIENTS: UsageClient[] = ["web", "ios", "android"];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function pct(n: number, d: number): string {
  if (d <= 0) return "0%";
  return `${Math.round((n / d) * 100)}%`;
}

function delta(current: number, previous: number): { label: string; tone: "up" | "down" | "flat" } {
  if (previous === 0 && current === 0) return { label: "no change", tone: "flat" };
  if (previous === 0) return { label: "new", tone: "up" };
  const change = Math.round(((current - previous) / previous) * 100);
  if (change === 0) return { label: "level with prior period", tone: "flat" };
  return { label: `${change > 0 ? "+" : ""}${change}% vs prior period`, tone: change > 0 ? "up" : "down" };
}

// ─── Client split bar ─────────────────────────────────────────────────────────

function ClientSplitBar({ values, total }: { values: Record<UsageClient, number>; total: number }) {
  const segments = CLIENTS.filter((k) => values[k] > 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", height: 10, borderRadius: "var(--radius-pill)", overflow: "hidden", background: "var(--surface-sunken)", gap: 2 }}>
        {segments.map((k) => (
          <div key={k} title={`${CLIENT_LABEL[k]}: ${values[k].toLocaleString()} (${pct(values[k], total)})`} style={{ flex: values[k], background: CLIENT_COLOR[k] }} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
        {CLIENTS.map((k) => (
          <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
            <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: CLIENT_COLOR[k] }} />
            {CLIENT_LABEL[k]}
            <span className="tnum" style={{ color: "var(--text-muted)" }}>{values[k].toLocaleString()} · {pct(values[k], total)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Timeseries (three lines, one axis) ───────────────────────────────────────

function ClientLines({ points, label }: { points: UsageTimeseriesPoint[]; label: string }) {
  const [hover, setHover] = React.useState<number | null>(null);
  const width = 640;
  const height = 160;
  const pad = { top: 12, right: 12, bottom: 24, left: 30 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...points.flatMap((p) => CLIENTS.map((k) => p[k])));
  const x = (i: number) => pad.left + (points.length > 1 ? (i / (points.length - 1)) * innerW : innerW / 2);
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const fmt = (d?: string) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
  const axisStyle: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", fill: "var(--text-muted)" };
  const hovered = hover != null ? points[hover] : null;

  return (
    <div style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)", padding: "12px 14px 8px", position: "relative" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4, gap: 12, flexWrap: "wrap" }}>
        <span className="eyebrow" style={{ fontSize: 10 }}>{label}</span>
        <div style={{ display: "flex", gap: 12 }}>
          {CLIENTS.map((k) => (
            <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text-secondary)" }}>
              <span aria-hidden="true" style={{ width: 12, height: 2, background: CLIENT_COLOR[k], borderRadius: 1 }} />
              {CLIENT_LABEL[k]}
              {hovered && <span className="tnum" style={{ color: "var(--text-muted)" }}>{hovered[k]}</span>}
            </span>
          ))}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={`${label} by client over ${points.length} days`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * width;
          const idx = Math.round(((px - pad.left) / innerW) * (points.length - 1));
          setHover(Math.max(0, Math.min(points.length - 1, idx)));
        }}
      >
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={pad.top + innerH * (1 - t)} y2={pad.top + innerH * (1 - t)} stroke="var(--border-subtle)" strokeWidth={1} />
            <text x={pad.left - 6} y={pad.top + innerH * (1 - t) + 3} textAnchor="end" style={axisStyle}>{Math.round(max * t)}</text>
          </g>
        ))}
        {CLIENTS.map((k) => (
          <polyline
            key={k}
            points={points.map((p, i) => `${x(i).toFixed(1)},${y(p[k]).toFixed(1)}`).join(" ")}
            fill="none"
            stroke={CLIENT_COLOR[k]}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {hover != null && (
          <g>
            <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} stroke="var(--border-strong)" strokeWidth={1} strokeDasharray="3 3" />
            {CLIENTS.map((k) => (
              <circle key={k} cx={x(hover)} cy={y(points[hover]![k])} r={4} fill={CLIENT_COLOR[k]} stroke="var(--surface-sunken)" strokeWidth={2} />
            ))}
          </g>
        )}
        <text x={pad.left} y={height - 6} style={axisStyle}>{fmt(points[0]?.date)}</text>
        <text x={width - pad.right} y={height - 6} textAnchor="end" style={axisStyle}>
          {hovered ? fmt(hovered.date) : fmt(points[points.length - 1]?.date)}
        </text>
      </svg>
    </div>
  );
}

// ─── Heatmap (day × hour) ─────────────────────────────────────────────────────

function DayHourHeatmap({ cells, timezone }: { cells: UsageHeatCell[]; timezone: string }) {
  const grid = React.useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
    for (const c of cells) {
      if (c.dow >= 0 && c.dow < 7 && c.hour >= 0 && c.hour < 24) g[c.dow]![c.hour] = c.views;
    }
    return g;
  }, [cells]);
  const max = Math.max(1, ...grid.flat());
  const level = (v: number) => (v === 0 ? 0 : Math.min(4, 1 + Math.floor((v / max) * 3.999)));

  return (
    <div className="r-scroll-x" style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: "34px repeat(24, 1fr)", gap: 3, minWidth: 560 }}>
        <span />
        {Array.from({ length: 24 }, (_, h) => (
          <span key={h} className="eyebrow" style={{ fontSize: 8, textAlign: "center", color: "var(--text-faint)" }}>
            {h % 3 === 0 ? String(h).padStart(2, "0") : ""}
          </span>
        ))}
        {grid.map((row, dow) => (
          <React.Fragment key={dow}>
            <span className="eyebrow" style={{ fontSize: 9, alignSelf: "center" }}>{DAY_LABELS[dow]}</span>
            {row.map((v, hour) => (
              <div
                key={hour}
                title={`${DAY_LABELS[dow]} ${String(hour).padStart(2, "0")}:00 (${timezone}): ${v} view${v === 1 ? "" : "s"}`}
                style={{ aspectRatio: "1 / 1", borderRadius: 3, background: `var(--heat-${level(v)})`, minWidth: 12 }}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── Retention grid ───────────────────────────────────────────────────────────

function RetentionGrid({ cohorts, weeks }: { cohorts: UsageCohort[]; weeks: number }) {
  if (cohorts.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No signup cohorts in this window.</p>;
  }
  const level = (p: number) => (p <= 0 ? 0 : p < 0.15 ? 1 : p < 0.35 ? 2 : p < 0.6 ? 3 : 4);
  return (
    <div className="r-scroll-x" style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 12, minWidth: 420 }}>
        <thead>
          <tr>
            <th className="eyebrow" style={{ fontSize: 9, textAlign: "left", padding: "0 6px 4px" }}>Cohort week</th>
            <th className="eyebrow" style={{ fontSize: 9, textAlign: "right", padding: "0 6px 4px" }}>Size</th>
            {Array.from({ length: weeks }, (_, i) => (
              <th key={i} className="eyebrow" style={{ fontSize: 9, textAlign: "center", padding: "0 2px 4px", minWidth: 40 }}>W{i}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((cohort) => (
            <tr key={cohort.cohortWeek}>
              <td className="tnum" style={{ padding: "0 6px", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                {new Date(cohort.cohortWeek).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </td>
              <td className="tnum" style={{ padding: "0 6px", textAlign: "right", color: "var(--text-muted)" }}>{cohort.size}</td>
              {cohort.retained.map((n, i) => {
                const share = cohort.size > 0 ? n / cohort.size : 0;
                const future = new Date(cohort.cohortWeek).getTime() + i * 7 * 86_400_000 > Date.now();
                return (
                  <td
                    key={i}
                    className="tnum"
                    title={future ? "Not yet" : `${n} of ${cohort.size} active in week ${i}`}
                    style={{
                      textAlign: "center",
                      padding: "5px 2px",
                      borderRadius: 3,
                      background: future ? "transparent" : `var(--heat-${level(share)})`,
                      color: future ? "var(--text-faint)" : share >= 0.35 ? "var(--text-primary)" : "var(--text-secondary)",
                    }}
                  >
                    {future ? "·" : `${Math.round(share * 100)}%`}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminUsagePage() {
  const { user } = useAuth();
  const [days, setDays] = React.useState<7 | 30 | 90>(30);
  const [includeAdmins, setIncludeAdmins] = React.useState(false);
  const [client, setClient] = React.useState<UsageClient | "all">("all");
  const [metric, setMetric] = React.useState<"visitors" | "views">("visitors");

  const q: UsageQuery = { days, includeAdmins, client: client === "all" ? null : client };
  const enabled = !!user?.isAdmin;

  const summaryQuery = useQuery({
    queryKey: ["admin/usage/summary", days, includeAdmins],
    queryFn: () => fetchUsageSummary({ days, includeAdmins }),
    enabled,
  });
  const seriesQuery = useQuery({
    queryKey: ["admin/usage/timeseries", days, includeAdmins, metric],
    queryFn: () => fetchUsageTimeseries({ days, includeAdmins }, metric),
    enabled,
  });
  const screensQuery = useQuery({
    queryKey: ["admin/usage/screens", days, includeAdmins, client],
    queryFn: () => fetchUsageScreens(q),
    enabled,
  });
  const heatQuery = useQuery({
    queryKey: ["admin/usage/heatmap", days, includeAdmins, client],
    queryFn: () => fetchUsageHeatmap(q),
    enabled,
  });
  const retentionQuery = useQuery({
    queryKey: ["admin/usage/retention", includeAdmins],
    queryFn: () => fetchUsageRetention(8, includeAdmins),
    enabled,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const summary = summaryQuery.data?.summary;
  const visitorsDelta = summary ? delta(summary.visitors, summary.previous.visitors) : null;
  const viewsDelta = summary ? delta(summary.views, summary.previous.views) : null;
  const clientViews = summary
    ? { web: summary.byClient.web.views, ios: summary.byClient.ios.views, android: summary.byClient.android.views }
    : null;
  const nothingYet = summary != null && summary.views === 0 && !includeAdmins;

  return (
    <AdminShell
      title="Usage"
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Segmented
            value={String(days) as "7" | "30" | "90"}
            options={[{ value: "7", label: "7 days" }, { value: "30", label: "30 days" }, { value: "90", label: "90 days" }]}
            onChange={(v) => setDays(Number(v) as 7 | 30 | 90)}
          />
          <button
            type="button"
            onClick={() => setIncludeAdmins((v) => !v)}
            aria-pressed={includeAdmins}
            style={{
              height: 30,
              padding: "0 12px",
              borderRadius: "var(--radius-pill)",
              border: `1px dashed ${includeAdmins ? "var(--border-gold)" : "var(--border-default)"}`,
              background: includeAdmins ? "var(--accent-soft)" : "transparent",
              color: includeAdmins ? "var(--text-accent)" : "var(--text-muted)",
              fontSize: 12,
              fontWeight: includeAdmins ? 600 : 500,
              cursor: "pointer",
            }}
          >
            {includeAdmins ? "Including admins" : "Admins hidden"}
          </button>
        </div>
      }
    >
      {/* ── Headline ─────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12 }}>
        {summaryQuery.isLoading ? (
          <>
            <Skel height={96} />
            <Skel height={96} />
            <Skel height={96} />
            <Skel height={96} />
          </>
        ) : summary ? (
          <>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Visitors" value={summary.visitors.toLocaleString()} sub={visitorsDelta?.label ?? null} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Visits" value={summary.visits.toLocaleString()} sub={`${summary.visitsPerVisitor} per visitor · 30 min gap`} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Screen views" value={summary.views.toLocaleString()} sub={viewsDelta?.label ?? null} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Views per visit" value={summary.viewsPerVisit.toLocaleString()} sub={summary.collectingSince ? `collecting since ${new Date(summary.collectingSince).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : "nothing collected yet"} />
            </AtlasPanel>
          </>
        ) : null}
      </div>

      {nothingYet && (
        <AtlasPanel>
          <EmptyState
            icon={<Activity size={20} />}
            title="No usage recorded yet"
            hint="Screen views arrive once the web build and app release with telemetry are live. Admin traffic is hidden by default; toggle it above to check the pipeline with your own visits."
          />
        </AtlasPanel>
      )}

      {/* ── Split + trend ─────────────────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 16 }}>
        <AtlasPanel title="Web vs app">
          {clientViews && summary ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <ClientSplitBar values={clientViews} total={summary.views} />
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {CLIENTS.map((k) => (
                  <div key={k} style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)", padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                      <ClientGlyph client={k} size={12} />
                      <span className="eyebrow" style={{ fontSize: 9 }}>{CLIENT_LABEL[k]}</span>
                    </div>
                    <div className="tnum" style={{ fontFamily: "var(--font-heading)", fontSize: 20, color: "var(--text-primary)" }}>
                      {summary.byClient[k].visitors.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>visitors</div>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
                A visitor using both web and the app counts once overall and once per client.
              </p>
            </div>
          ) : (
            <Skel height={140} />
          )}
        </AtlasPanel>

        <AtlasPanel
          title="Daily trend"
          action={
            <Segmented
              value={metric}
              options={[{ value: "visitors", label: "Visitors" }, { value: "views", label: "Views" }]}
              onChange={setMetric}
            />
          }
        >
          {seriesQuery.data ? (
            <ClientLines points={seriesQuery.data.points} label={metric === "visitors" ? "Daily visitors" : "Daily screen views"} />
          ) : (
            <Skel height={180} />
          )}
        </AtlasPanel>
      </div>

      {/* ── Screens + heatmap ─────────────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)", gap: 16 }}>
        <AtlasPanel
          title="Screens"
          action={
            <Segmented
              value={client}
              options={[{ value: "all", label: "All" }, { value: "web", label: "Web" }, { value: "ios", label: "iOS" }, { value: "android", label: "Android" }]}
              onChange={setClient}
            />
          }
        >
          {screensQuery.isLoading && <Skel height={220} />}
          {screensQuery.data && screensQuery.data.screens.length === 0 && (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No screen views in this window.</p>
          )}
          {screensQuery.data && screensQuery.data.screens.length > 0 && (
            <div className="r-scroll-x" style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 460 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <th className="eyebrow" style={{ fontSize: 9, textAlign: "left", padding: "0 0 8px" }}>Screen</th>
                    <th className="eyebrow" style={{ fontSize: 9, textAlign: "right", padding: "0 0 8px" }}>Views</th>
                    <th className="eyebrow" style={{ fontSize: 9, textAlign: "right", padding: "0 0 8px" }}>Visitors</th>
                    <th className="eyebrow" style={{ fontSize: 9, textAlign: "left", padding: "0 0 8px 12px", width: "36%" }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {screensQuery.data.screens.slice(0, 40).map((row) => {
                    const total = Math.max(1, row.views);
                    return (
                      <tr key={row.name} style={{ borderBottom: "1px solid var(--border-subtle)" }} title={row.paths.join(", ")}>
                        <td style={{ padding: "7px 0", color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
                          {screenLabel(row.name)}
                        </td>
                        <td className="tnum" style={{ padding: "7px 0", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 }}>{row.views.toLocaleString()}</td>
                        <td className="tnum" style={{ padding: "7px 0", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>{row.visitors.toLocaleString()}</td>
                        <td style={{ padding: "7px 0 7px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 8, borderRadius: "var(--radius-pill)", background: "var(--surface-sunken)", overflow: "hidden", display: "flex", gap: 1 }}>
                              <div style={{ width: `${Math.round(row.share * 100)}%`, display: "flex", gap: 1 }}>
                                {CLIENTS.filter((k) => row.byClient[k] > 0).map((k) => (
                                  <div key={k} title={`${CLIENT_LABEL[k]} ${row.byClient[k]}`} style={{ flex: row.byClient[k] / total, background: CLIENT_COLOR[k] }} />
                                ))}
                              </div>
                            </div>
                            <span className="tnum" style={{ fontSize: 11, color: "var(--text-muted)", width: 34, textAlign: "right" }}>{Math.round(row.share * 100)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </AtlasPanel>

        <AtlasPanel title="When people use Atlas">
          {heatQuery.data ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <DayHourHeatmap cells={heatQuery.data.cells} timezone={heatQuery.data.timezone} />
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
                Screen views by day and hour, {heatQuery.data.timezone}. Darker is busier.
              </p>
            </div>
          ) : (
            <Skel height={180} />
          )}
        </AtlasPanel>
      </div>

      {/* ── Retention ─────────────────────────────────────────────────── */}
      <AtlasPanel title="Retention by signup week">
        {retentionQuery.data ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <RetentionGrid cohorts={retentionQuery.data.cohorts} weeks={retentionQuery.data.weeks} />
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
              Share of each signup cohort that opened Atlas in week N after joining. Built from the nightly rollup, so today is not counted yet.
            </p>
          </div>
        ) : (
          <Skel height={160} />
        )}
      </AtlasPanel>
    </AdminShell>
  );
}
